/**
 * Bundly Probierpaket: apply defu product-card palette plates.
 *
 * Requires Bundly block setting Custom data metafields:
 *   card:custom.pdp_palette,card:custom.product_card_short_info
 *
 * - pdp_palette is hidden and drives the media plate colors
 * - product_card_short_info is shown once via Bundly’s own metafield output (styled in CSS)
 *
 * Metafield custom.pdp_palette must be Storefront-accessible.
 */
const PALETTES = {
  huhn: { bg: "#FFF1D5", accent: "#FEDFAA" },
  pute: { bg: "#E4F6E2", accent: "#CBEBC7" },
  schwein: { bg: "#FCE7F5", accent: "#FCCEED" },
  rind: { bg: "#FECBCA", accent: "#FDA6A4" },
  ente: { bg: "#D3F0ED", accent: "#A7E1DD" },
  ente_alt: { bg: "#F9EAF1", accent: "#F4D6E4" },
  lachs: { bg: "#FCE7E4", accent: "#FBD3CD" },
  yak: { bg: "#D2E8FF", accent: "#AED7FF" },
  gans: { bg: "#FCF1C5", accent: "#FBE18D" },
  lamm: { bg: "#FED3CA", accent: "#FCB4A5" },
  // Legacy / choice-list aliases
  cream: { bg: "#FFF1D5", accent: "#FEDFAA" },
  green: { bg: "#E4F6E2", accent: "#CBEBC7" },
  pink: { bg: "#FCE7F5", accent: "#FCCEED" },
  blue: { bg: "#E2EDF7", accent: "#CCE0F1" },
  junior: { bg: "#E2EDF7", accent: "#CCE0F1" },
};

const ACCENT_PATH =
  "M37.9971 45.7591C43.3044 39.6273 49.4475 34.2079 56.201 29.5646C68.6333 21.0285 87.4475 9.14659 110.773 3.33287C136.5 -3.06604 205.839 -3.48585 267.562 32.1598C273.374 35.5183 278.747 39.5128 283.657 44.0035C299.233 58.2516 330.095 92.2052 341.612 145.865C357.295 218.924 349.374 246.008 331.157 273.359C313.696 299.566 259.442 320.582 204.91 324.932C150.378 329.27 80.5613 321.345 39.0586 283.384C-2.44416 245.41 -5.00491 175.213 4.70737 123.64C12.9602 79.8018 22.8316 63.2511 37.9971 45.7591Z";

const PALETTE_KEYS = new Set(Object.keys(PALETTES));

/**
 * @param {HTMLElement} imageWrap
 */
function ensurePlate(imageWrap) {
  if (imageWrap.querySelector(".we-bundly-media-plate")) return;

  const plate = document.createElement("div");
  plate.className = "we-bundly-media-plate";
  plate.setAttribute("aria-hidden", "true");

  const accent = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  accent.setAttribute("class", "we-bundly-media-accent");
  accent.setAttribute("viewBox", "0 0 350 326");
  accent.setAttribute("fill", "none");
  accent.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  accent.setAttribute("focusable", "false");
  accent.setAttribute("preserveAspectRatio", "none");

  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", ACCENT_PATH);
  path.setAttribute("fill", "var(--pcard-accent)");
  accent.appendChild(path);
  plate.appendChild(accent);

  imageWrap.insertBefore(plate, imageWrap.firstChild);
}

/**
 * @param {HTMLElement} card
 * @returns {{ palette: string }}
 */
function readCardMetafields(card) {
  let palette = "";

  card.querySelectorAll(".bundly__product_custom_data").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    const raw = (node.textContent || "").trim();
    const key = raw.toLowerCase().replace(/[\s-]+/g, "_");

    if (PALETTE_KEYS.has(key)) {
      palette = key;
      node.classList.add("we-bundly-palette-source");
    }
  });

  return { palette };
}

/**
 * @param {HTMLElement} card
 */
function enhanceCard(card) {
  if (!(card instanceof HTMLElement)) return;

  // Remove legacy duplicate subtitle nodes from older script versions
  card.querySelectorAll(".we-bundly-subtitle").forEach((el) => el.remove());

  const { palette } = readCardMetafields(card);
  const colors = palette ? PALETTES[palette] : { bg: "#ffffff", accent: "#f3f3f3" };

  card.style.setProperty("--pcard-bg", colors.bg);
  card.style.setProperty("--pcard-accent", colors.accent);
  if (palette) card.dataset.weBundlyPalette = palette;
  else delete card.dataset.weBundlyPalette;

  const imageWrap = card.querySelector(".bundly__component_image");
  if (imageWrap instanceof HTMLElement) {
    imageWrap.style.setProperty("--pcard-bg", colors.bg);
    imageWrap.style.setProperty("--pcard-accent", colors.accent);
    ensurePlate(imageWrap);
  }

  // Options dialog lives inside the card — paint its media with the same palette
  enhanceDialogMedias(card, colors);

  card.dataset.weBundlyReady = "true";
}

/**
 * @param {HTMLElement} scope Card or dialog root
 * @param {{ bg: string, accent: string } | null} [colors]
 */
function enhanceDialogMedias(scope, colors = null) {
  const medias = scope.querySelectorAll(".bundly__product_media");
  if (!medias.length) return;

  let resolved = colors;
  if (!resolved) {
    const card = scope.closest?.(
      "bundly-collection-component-card, bundly-product-component-card, .bundly__component_grid_item"
    );
    const fromCard = card instanceof HTMLElement ? card.dataset.weBundlyPalette : "";
    const { palette } = fromCard
      ? { palette: fromCard }
      : readCardMetafields(scope instanceof HTMLElement ? scope : document.createElement("div"));
    resolved = palette && PALETTES[palette] ? PALETTES[palette] : { bg: "#ffffff", accent: "#f3f3f3" };
  }

  medias.forEach((media) => {
    if (!(media instanceof HTMLElement)) return;
    media.style.setProperty("--pcard-bg", resolved.bg);
    media.style.setProperty("--pcard-accent", resolved.accent);
    media.classList.add("we-bundly-media--paletted");
    ensurePlate(media);
  });
}

function enhanceAll() {
  document
    .querySelectorAll(
      ".bundly__block bundly-collection-component-card, .bundly__block bundly-product-component-card, .bundly__block .bundly__component_grid_item"
    )
    .forEach((el) => {
      if (el instanceof HTMLElement) enhanceCard(el);
    });

  // Dialogs that may hydrate after the card pass
  document.querySelectorAll(".bundly__block .bundly__product_modal").forEach((modal) => {
    if (modal instanceof HTMLElement) enhanceDialogMedias(modal);
  });
}

/**
 * Mark clicked modal thumbnails as active (Bundly does not expose a selected class).
 * @param {HTMLElement} root
 */
function bindModalThumbState(root) {
  if (root.dataset.weBundlyModalThumbs === "true") return;
  root.dataset.weBundlyModalThumbs = "true";

  root.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const thumb = target.closest(".bundly__product_medias .bundly__product_media:not(:first-child)");
    if (!thumb) return;
    const gallery = thumb.closest(".bundly__product_medias");
    if (!gallery) return;
    gallery.querySelectorAll(".bundly__product_media.is-active").forEach((el) => {
      el.classList.remove("is-active");
    });
    thumb.classList.add("is-active");
  });
}

function init() {
  const root = document.querySelector(".bundly__block");
  if (!root) return;

  enhanceAll();
  bindModalThumbState(root);

  const observer = new MutationObserver(() => {
    enhanceAll();
  });
  observer.observe(root, { childList: true, subtree: true });

  // Bundly hydrates cards + metafields after first paint
  let tries = 0;
  const timer = setInterval(() => {
    enhanceAll();
    tries += 1;
    if (tries >= 20) clearInterval(timer);
  }, 400);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}
